


import { Component, Vue } from "vue-property-decorator";

import { CustomNotification, SubscriptionMigration } from "App/Models";
import { Subscriptions as SubscriptionsService } from "App";

import { EventBus } from "App/IOC";

type StatusFilter = "All" | "Migrated" | "Failed" | "Skipped" | "UpToDate" | "OutOfScope";

/* One line of a billing snapshot stored on a Migrated row: label, quantity, pinned unit price and
   the line subtotal — enough to read "Utilisateurs: 3 x 45 = 135". Quantity / Unit are absent on
   rows logged before the snapshot carried them. */
interface SnapshotLine {
    Label: string;
    Quantity?: number;
    Unit?: number;
    Amount: number;
    /* Included feature (billed 0) -> shown "Inclus jusqu'à {Quantity}" like the desktop view. */
    Included?: boolean;
    /* Price + pinned price-version the line billed on. Shown for audit so the version move
       (before vN -> after vM) is visible. Absent on rows logged before they were traced. */
    PriceId?: number;
    PriceVersionId?: number;
}

/* Parsed billing snapshot (recurrence, per-line amount, total) of a subscription before / after a
   migration. */
interface Snapshot {
    Recurrence?: string;
    Total: number;
    Lines: SnapshotLine[];
}

/* One row of the before / after detail table: the same line on both sides, with its quantity / unit
   / subtotal on each side so the migration's effect is visible per line. Changed flags the lines
   whose subtotal or unit price actually moved, so the control reads at a glance. */
interface DetailRow {
    Label: string;
    Before?: SnapshotLine;
    After?: SnapshotLine;
    Changed: boolean;
}

@Component
export default class MigrationHistory extends Vue {
    private entries: SubscriptionMigration[] = [];
    private loading: boolean = true;
    private filter: StatusFilter = "All";

    private page: number = 1;
    private pages: number = 1;
    private total: number = 0;

    /* SubscriptionId of the row whose before / after detail is expanded (one at a time). -1 = none.
       Init to a real number, NOT undefined: a field the class inits to undefined is not made
       reactive by the tsc/babel transpile here, so toggling it updated the value but never
       re-rendered (the "Détail does nothing" bug). */
    private expandedId: number = -1;

    /* SubscriptionId currently being replayed, to disable its button and show a pending label.
       -1 = none (same reactivity reason as expandedId above). */
    private retryingId: number = -1;

    private async mounted(): Promise<void> {
        await this.load();
    }

    private get filters(): StatusFilter[] {
        return [ "All", "Migrated", "Failed", "Skipped", "UpToDate", "OutOfScope" ];
    }

    /* Pagination and the status filter are both server-side, so the pager stays correct under the
       filter (a client-side filter would only narrow the current page). */
    private async load(): Promise<void> {
        this.loading = true;

        const status = this.filter === "All" ? undefined : this.filter;

        const [ result, error ] =
            await SubscriptionsService.MigrationHistory( this.page, 20, status );

        if( error === undefined && result !== undefined ) {
            this.entries = result.Items;
            this.page = result.Page;
            this.pages = result.Pages;
            this.total = result.Total;
        }

        this.expandedId = -1;
        this.loading = false;
    }

    private async selectFilter( value: StatusFilter ): Promise<void> {
        if( this.filter === value )
            return;

        this.filter = value;
        this.page = 1;

        await this.load();
    }

    private async previousPage(): Promise<void> {
        if( this.page <= 1 )
            return;

        this.page--;
        await this.load();
    }

    private async nextPage(): Promise<void> {
        if( this.page >= this.pages )
            return;

        this.page++;
        await this.load();
    }

    private filterLabel( value: StatusFilter ): string {
        return this.$t( `SUBSCRIPTIONMIGRATION_filter${value}` ).toString();
    }

    private statusLabel( status: string ): string {
        return this.$t( `SUBSCRIPTIONMIGRATION_status${status}` ).toString();
    }

    private statusClass( status: string ): string {
        return `c-migration-history__status--${status.toLowerCase()}`;
    }

    /* Plain-language reason for the outcome, shown atop the expanded panel so the row reads on its
       own (key-based, so every status maps to its own text). */
    private explainText( status: string ): string {
        return this.$t( `SUBSCRIPTIONMIGRATION_explain${status}` ).toString();
    }

    /* Replay the migration of a Failed subscription. Logs a fresh history row server-side, so on
       success the list is reloaded to show the new outcome and the Plans tab is asked to refresh
       its "prices not applied" labels. */
    private async retry( entry: SubscriptionMigration ): Promise<void> {
        if( this.retryingId !== -1 )
            return;

        this.retryingId = entry.SubscriptionId;

        const [ result, error ] =
            await SubscriptionsService.RetryMigration( entry.SubscriptionId, this.$i18n.locale );

        this.retryingId = -1;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        if( result !== undefined && result.Migrated ) {
            EventBus.Publish( "SubscriptionsMigrated" );
            EventBus.Publish( "Notification", CustomNotification.Success(
                this.$t( "SUBSCRIPTIONMIGRATION_retryTitle" ).toString(),
                this.$t( "SUBSCRIPTIONMIGRATION_retryDone" ).toString()
            ));

        } else {
            const reason = ( result !== undefined && result.Message !== undefined )
                ? result.Message
                : this.$t( "SUBSCRIPTIONMIGRATION_retryFailed" ).toString();

            EventBus.Publish( "Notification", CustomNotification.Failure(
                this.$t( "SUBSCRIPTIONMIGRATION_retryTitle" ).toString(), reason
            ));
        }

        await this.load();
    }

    private parseSnapshot( raw?: string ): Snapshot | undefined {
        if( raw === undefined || raw === "" )
            return undefined;

        try {
            return JSON.parse( raw ) as Snapshot;

        } catch( exception ) {
            return undefined;
        }
    }

    private hasDetail( entry: SubscriptionMigration ): boolean {
        return entry.Status === "Migrated"
            && entry.Before !== undefined
            && entry.After !== undefined;
    }

    private toggleDetail( entry: SubscriptionMigration ): void {
        this.expandedId = this.expandedId === entry.SubscriptionId
            ? -1
            : entry.SubscriptionId;
    }

    /* Merge the before / after snapshots into one row per line, so the detail table shows the old
       and new amount side by side. Lines are paired by position (the migration keeps the same
       lines, only the amounts move). */
    private detailRows( entry: SubscriptionMigration ): DetailRow[] {
        const before = this.parseSnapshot( entry.Before );
        const after = this.parseSnapshot( entry.After );

        const beforeLines = before !== undefined ? before.Lines : [];
        const afterLines = after !== undefined ? after.Lines : [];
        const count = Math.max( beforeLines.length, afterLines.length );

        const rows: DetailRow[] = [];

        for( let index = 0; index < count; index++ ) {
            const beforeLine = beforeLines[ index ];
            const afterLine = afterLines[ index ];

            const beforeAmount = beforeLine !== undefined ? beforeLine.Amount : 0;
            const afterAmount = afterLine !== undefined ? afterLine.Amount : 0;
            const beforeUnit = beforeLine !== undefined ? beforeLine.Unit : undefined;
            const afterUnit = afterLine !== undefined ? afterLine.Unit : undefined;

            rows.push({
                Label: afterLine !== undefined ? afterLine.Label : beforeLine.Label,
                Before: beforeLine,
                After: afterLine,
                Changed: beforeAmount !== afterAmount || beforeUnit !== afterUnit
            });
        }

        /* Paying lines (a non-zero amount on either side) float to the top; the 0 € / included
           lines sink to the bottom. Stable sort keeps each group in its original line order. */
        const isPaying = ( row: DetailRow ): boolean => {
            const beforeAmount = row.Before !== undefined ? row.Before.Amount : 0;
            const afterAmount = row.After !== undefined ? row.After.Amount : 0;

            return beforeAmount !== 0 || afterAmount !== 0;
        };

        return rows.sort(
            ( a: DetailRow, b: DetailRow ): number =>
                Number( isPaying( b ) ) - Number( isPaying( a ) )
        );
    }

    /* "3 x 45,00 = 135,00" when the quantity / unit are known (snapshot logged after the unit price
       was added), else just the subtotal, else "—". Surfaces the per-user price move. */
    private formatLine( line?: SnapshotLine ): string {
        if( line === undefined )
            return "—";

        /* Included feature: mirror the desktop "Produits et prix" view ("Inclus jusqu'à N"). */
        if( line.Included === true )
            return this.$t(
                "SUBSCRIPTIONMIGRATION_included", { quantity: line.Quantity }
            ).toString();

        const version = ( line.PriceVersionId !== undefined )
            ? `v${ line.PriceVersionId } · `
            : "";

        /* Quantity 1 (base / package price): just the amount, no "1 × ... =" noise. */
        if( line.Quantity === 1 || line.Quantity === undefined || line.Unit === undefined )
            return `${ version }${ this.formatAmount( line.Amount ) }`;

        return `${ version }${ line.Quantity } × ${ this.formatAmount( line.Unit ) }`
            + ` = ${ this.formatAmount( line.Amount ) }`;
    }

    private snapshotTotal( raw?: string ): number | undefined {
        const snapshot = this.parseSnapshot( raw );

        return snapshot !== undefined ? snapshot.Total : undefined;
    }

    private recurrenceLabel( raw?: string ): string {
        const snapshot = this.parseSnapshot( raw );

        if( snapshot === undefined || snapshot.Recurrence === undefined )
            return "";

        return snapshot.Recurrence === "Yearly"
            ? this.$t( "SUBSCRIPTIONS_recurrenceYearly" ).toString()
            : this.$t( "SUBSCRIPTIONS_recurrenceMonthly" ).toString();
    }

    private formatAmount( amount?: number ): string {
        if( amount === undefined )
            return "—";

        /* Every amount on this screen is a subscription-line / plan price snapshot — catalogue
           prices, always excluding tax. */
        return `${ amount.toFixed( 2 ).replace( ".", "," ) } €`
            + ` ${ this.$t( "GLOBAL_excludingTaxSuffix" ) }`;
    }
}
